import {
	RiArrowLeftLine,
	RiCalendarLine,
	RiDoorOpenLine,
	RiGovernmentLine,
	RiMapPinLine,
	RiTicket2Line,
	RiTimeLine,
} from "@remixicon/react";
import {
	formatLongDateWithPreferences,
	formatShowTimeRange,
	formatTimeWithPreferences,
	getShowDisplayParts,
	type ProcessedShow,
} from "@workspace/shared-utils";
import { Button } from "@workspace/ui/components/button";
import { Card, CardHeader } from "@workspace/ui/components/card";
import { useRouter } from "next/navigation";
import { memo, useState } from "react";
import { ShowTourSelector } from "@/components/features/show/navigation/show-tour-selector";
import { useUserPreferences } from "@/hooks/shared/use-user-preferences";
import { ShowAttendanceButton } from "@/components/features/show/buttons/show-attendance-button";
import { VenueDetailModal } from "@/components/shared/venue-detail-modal";
import { ShowPhoto } from "./shared/show-photo";

interface Venue {
	id: string;
	name: string;
	slug: string;
	address?: string;
	city: string;
	state?: string;
	country: string;
	capacity?: number;
	venue_type?: string;
}

interface Show {
	id: string;
	title: string;
	artist_name: string;
	artist_id?: string;
	date: string;
	time_start?: string;
	time_end?: string;
	door_open_time?: string;
	location: string;
	age_limit?: string;
	poster_url?: string;
	slug: string;
	venue_id: string;
	venue: Venue;
	tour?: string | null;
	day?: string | null;
	website_url?: string | null;
	ticket_url?: string | null;
	presents_name?: string | null;
	is_collaborative?: boolean;
	collaboration_format?: "×" | "x" | "B2B" | "+" | "&" | "vs." | ",";
	collaboration_alias?: string | null;
	show_artists?: Array<{
		id: string;
		position: number;
		time_start?: string;
		time_end?: string;
		is_headliner: boolean;
		role: "headliner" | "collaborator" | "supporting_artist";
		artist: {
			id: string;
			name: string;
			image_url?: string | null;
			[key: string]: unknown;
		};
	}>;
}

interface ShowHeaderProps {
	show: Show;
}

export const ShowHeader = memo(function ShowHeader({ show }: ShowHeaderProps) {
	const router = useRouter();
	const userPreferences = useUserPreferences();
	const [showVenueModal, setShowVenueModal] = useState(false);
	const [showMapModal, setShowMapModal] = useState(false);

	// Get display parts for collaboration handling
	const displayParts = getShowDisplayParts(show as unknown as ProcessedShow);

	return (
		<>
			{/* Navigation - simplified to match set detail structure */}
			<div className="flex items-center justify-between gap-2">
				<Button
					variant="secondary"
					size="sm"
					onClick={() => {
						if (window.history.length > 1) {
							router.back();
						} else {
							router.push("/shows");
						}
					}}
					className="inline-flex items-center text-sm"
				>
					<RiArrowLeftLine className="mr-1 h-4 w-4" />
					<span>Back</span>
				</Button>
				{show.tour && (
					<ShowTourSelector
						currentShow={{
							id: show.id,
							title: show.title,
							slug: show.slug,
							tour: show.tour,
							day: show.day,
						}}
					/>
				)}
			</div>

			{/* Show Detail Card */}
			<Card className="h-full">
				<CardHeader className="h-full p-3">
					<div className="grid grid-cols-[auto_1fr] gap-4">
						{/* Show poster - always visible on left, responsive sizing like festival card */}
						<div className="relative w-32 md:w-40 lg:w-48">
							<div className="aspect-square">
								<ShowPhoto
									src={
										show.poster_url ||
										show.show_artists?.find((sa) => sa.is_headliner)?.artist
											?.image_url
									}
									alt={`${show.artist_name} at ${show.venue?.name || show.location}`}
									sizes="(max-width: 640px) 8rem, (max-width: 768px) 10rem, 12rem"
									rounded="lg"
									priority={true}
									iconSize="lg"
								/>
							</div>
						</div>

						{/* Show info */}
						<div className="min-w-0">
							{/* Title */}
							<h1 className="mb-3 font-bold text-xl leading-tight">
								{displayParts.parts && displayParts.parts.length > 0 ? (
									// Multi-artist collaboration (3+ artists)
									displayParts.parts.map((part, index) => {
										const key = `${show.id}-part-${index}`;

										if (part.type === "artist") {
											return <span key={key}>{part.text}</span>;
										} else {
											return (
												<span
													key={key}
													className="ml-1 font-normal text-muted-foreground/50"
												>
													{part.text}
												</span>
											);
										}
									})
								) : (
									// Standard display (single artist, presents, or two-artist collaboration)
									<>
										{displayParts.mainText}
										{displayParts.connector && (
											<span className="ml-1 font-normal text-muted-foreground/50">
												{displayParts.connector}
											</span>
										)}
										{displayParts.secondaryText && (
											<>
												{displayParts.connector && " "}
												{displayParts.secondaryText}
											</>
										)}
									</>
								)}
							</h1>

							<div className="space-y-3">
								<div className="flex items-center gap-2 text-base">
									<RiGovernmentLine className="h-4 w-4 flex-shrink-0" />
									{show.venue ? (
										<button
											onClick={() => setShowVenueModal(true)}
											className="cursor-pointer leading-tight text-left text-primary underline hover:text-primary/80"
										>
											{show.venue.name}
										</button>
									) : (
										<span className="leading-tight text-left">Venue TBD</span>
									)}
								</div>

								{show.venue && (
									<div className="flex items-center gap-2 text-base">
										<RiMapPinLine className="h-4 w-4 flex-shrink-0" />
										<button
											type="button"
											onClick={() => setShowMapModal(true)}
											className="cursor-pointer leading-tight text-left text-primary underline hover:text-primary/80"
										>
											{(() => {
												// Build address carefully to avoid duplicates
												const addressParts = [];

												// Start with the street address
												if (show.venue.address) {
													addressParts.push(show.venue.address);
												}

												// Add city only if it's not already in the address
												if (show.venue.city) {
													const addressStr = addressParts
														.join(" ")
														.toLowerCase();
													if (
														!addressStr.includes(
															show.venue.city.toLowerCase(),
														)
													) {
														addressParts.push(show.venue.city);
													}
												}

												// Add state only if it's not already included
												if (show.venue.state) {
													const addressStr = addressParts
														.join(" ")
														.toLowerCase();
													if (
														!addressStr.includes(
															show.venue.state.toLowerCase(),
														)
													) {
														addressParts.push(show.venue.state);
													}
												}

												// Join and then remove any obvious duplicates
												let result = addressParts.join(", ");

												// Remove duplicate patterns like "Los Angeles, CA, US, Los Angeles, CA, US"
												const duplicatePattern =
													/,\s*([^,]+,\s*[^,]+,\s*[^,]+),\s*\1/g;
												result = result.replace(duplicatePattern, ", $1");

												// Also remove simple duplicates like "Los Angeles, Los Angeles"
												const simpleDuplicatePattern = /,\s*([^,]+),\s*\1/g;
												result = result.replace(
													simpleDuplicatePattern,
													", $1",
												);

												return result;
											})()}
										</button>
									</div>
								)}

								<div className="flex items-center gap-2 text-base">
									<RiCalendarLine className="h-4 w-4 flex-shrink-0" />
									<span className="leading-tight text-left">
										{formatLongDateWithPreferences(show.date, userPreferences)}
									</span>
								</div>

								<div className="flex items-center gap-2 text-base">
									<RiTimeLine className="h-4 w-4 flex-shrink-0" />
									<span className="leading-tight text-left">
										{formatShowTimeRange(
											show.time_start,
											show.time_end,
											userPreferences,
										)}
									</span>
								</div>

								{/* Door Open Time */}
								{show.door_open_time && (
									<div className="flex items-center gap-2 text-base">
										<RiDoorOpenLine className="h-4 w-4 flex-shrink-0" />
										<span className="leading-tight text-left">
											{formatTimeWithPreferences(
												show.door_open_time,
												userPreferences,
											)}
										</span>
									</div>
								)}
							</div>

							{/* Action Buttons - at bottom */}
							<div className="mt-4 flex gap-2">
								<ShowAttendanceButton
									showId={show.id}
									showIsPast={(() => {
										// Parse date without timezone conversion
										const parts = show.date.split("-");
										const year = Number(parts[0]);
										const month = Number(parts[1]);
										const day = Number(parts[2]);
										const showDate = new Date(year, month - 1, day);
										showDate.setHours(23, 59, 59, 999);
										return showDate < new Date();
									})()}
									size="sm"
								/>
								{show.ticket_url && (
									<Button
										variant="default"
										size="sm"
										asChild
									>
										<a
											href={show.ticket_url}
											target="_blank"
											rel="noopener noreferrer"
											className="inline-flex items-center gap-2"
										>
											<RiTicket2Line className="h-4 w-4" />
											<span>Buy Ticket</span>
										</a>
									</Button>
								)}
							</div>
						</div>
					</div>
				</CardHeader>
			</Card>

			{/* Venue detail modal */}
			{show.venue && (
				<VenueDetailModal
					isOpen={showVenueModal}
					venue={show.venue}
					onClose={() => setShowVenueModal(false)}
				/>
			)}

			{/* Map modal */}
			{show.venue && (
				<VenueDetailModal
					isOpen={showMapModal}
					venue={show.venue}
					onClose={() => setShowMapModal(false)}
					initialView="map"
				/>
			)}
		</>
	);
});
